import type { EnvironmentInjector, InjectionToken, Injector } from '@angular/core';
import { IResourceContext, RESOURCE_CONTEXT_DATA_TOKEN } from 'app/multi-contexts/resource-context.typings';
import { BaseAppModuleManagementService } from 'app/multi-contexts/services/base-app-module-management.service';
import { isObject, isString } from 'lodash';

export class RootApplication {
    private static _injector: Injector;

    public static get injector(): Injector {
        return this._injector;
    }

    public static initialize(value: Injector): void {
        this._injector = value;
    }
}

function isResourceContext(context: unknown): context is IResourceContext {
    return (
        isObject(context) &&
        isString(Reflect.get(context, 'contextId')) &&
        isString(Reflect.get(context, 'contextType'))
    );
}

export async function getServiceInstance<R>(
    context: EnvironmentInjector | IResourceContext,
    serviceName: string | InjectionToken<R>
): Promise<R> {
    let injector: Injector;
    let contextData: IResourceContext;
    if (isResourceContext(context)) {
        injector = RootApplication.injector;
        contextData = context;
    } else {
        injector = context;
        contextData = context.get<IResourceContext>(RESOURCE_CONTEXT_DATA_TOKEN);
    }
    return multiContextRootModuleAsEntry
        ? injector
              .get<BaseAppModuleManagementService>(BaseAppModuleManagementService)
              .getScopedService<R>(contextData, serviceName)
        : injector.get(serviceName);
}

/**
 * Return the cached service of a context or undefined if not existed yet
 * @param context the context data
 * @param serviceName the service name or its ng token
 */
export function getCachedServiceInstanceSync<R>(
    context: IResourceContext,
    serviceName: string | InjectionToken<R>
): R | undefined {
    const injector: Injector = RootApplication.injector;
    return multiContextRootModuleAsEntry
        ? injector
            .get<BaseAppModuleManagementService>(BaseAppModuleManagementService)
            .getCachedScopedServiceSync<R>(context, serviceName)
        : injector.get(serviceName);
}
